'use client';

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { fetchWithCsrf } from '@/lib/csrf';
import { apiErrorMessage } from '@/lib/api-error';

/**
 * "Create a free account" for someone who has just confirmed their email.
 *
 * WHY THIS EXISTS AS A BUTTON RATHER THAN AS A SIDE EFFECT
 * The confirmation click could have created an account outright — the address
 * is proven by then, which is the same thing a magic link proves. It
 * deliberately does not, for two reasons:
 *
 *  1. Security. The link that proved the address is a GET, arrives by email,
 *     and stays replayable for seven days, because a download link has to
 *     survive being clicked twice and being prefetched by a corporate scanner.
 *     Anything it can authorise, a forwarded email can authorise. A free PDF is
 *     an acceptable thing to lose that way. An account is not.
 *  2. Consent. Someone who wanted a pitch deck template did not ask to be given
 *     a user account, and quietly creating one is a scope nobody agreed to.
 *
 * So the account comes from a real magic link — single-use and short-lived —
 * and what the confirmation buys is that nobody has to type their address
 * again. One click, no form. See app/api/lead/claim-account/route.ts.
 */

const P = 'pageContent.leadCapture.claim';

interface ClaimAccountPromptProps {
  /** Render only for a confirmed visitor who has no session. */
  show: boolean;
  /** Magnet key, so the login lands back on the page they were reading. */
  source: string;
}

export function ClaimAccountPrompt({ show, source }: ClaimAccountPromptProps) {
  const { t } = useTranslation();
  // English is the fallback for every key, so a missing translation renders English.
  const s = (key: string, fallback: string): string => t(`${P}.${key}`, { defaultValue: fallback });
  const [state, setState] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);

  if (!show) return null;

  async function onClaim() {
    if (state === 'sending') return;
    setState('sending');
    setError(null);
    try {
      // proxy.ts rejects unexempted POSTs without the CSRF header.
      const res = await fetchWithCsrf('/api/lead/claim-account', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ source }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) {
        setError(apiErrorMessage(t, data, s('errGeneric', 'Something went wrong.')));
        setState('error');
        return;
      }
      setState('sent');
    } catch {
      setError(s('errNetwork', 'Network error. Please try again.'));
      setState('error');
    }
  }

  if (state === 'sent') {
    return (
      <div className="mt-8 rounded-xl border border-green-200 bg-green-50 p-5 text-center">
        <p className="text-sm font-semibold text-green-900">
          {s('sentTitle', 'Check your inbox.')}
        </p>
        <p className="mt-1 text-sm text-green-800">
          {s(
            'sentBody',
            'We sent a sign-in link. Opening it creates your free account — still no password.',
          )}
        </p>
      </div>
    );
  }

  return (
    <div className="mt-8 rounded-xl border border-gray-200 bg-white p-5 text-center">
      <p className="text-sm font-semibold text-gray-900">{s('title', 'Want to keep this?')}</p>
      <p className="mx-auto mt-1 max-w-md text-sm text-gray-600">
        {s(
          'body',
          'Create a free account and your downloads, saved investors and match history stay in one place. No password, and we already have your address — one click.',
        )}
      </p>
      <button
        type="button"
        onClick={onClaim}
        disabled={state === 'sending'}
        className="mt-4 rounded-lg bg-primary-600 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-primary-700 disabled:opacity-60"
      >
        {state === 'sending' ? s('sending', 'Sending…') : s('cta', 'Create my free account')}
      </button>
      {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
    </div>
  );
}
